import { engineComponent, NumberUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { Communication } from "neo-games";
import { Symbol } from "../GameComponents/Symbol";
import { Symbols } from "../GameComponents/Symbols";
import { CustomUtils } from "../Utils/CustomUtils";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Paylines } from "../GameComponents/Paylines";
import { Languages } from "../Utils/Languages";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { ImageTextElement } from "../Components/Elements/Texts/ImageTextElement";
import { BaseView } from "../GameComponents/Base/BaseView";
import { AppContext } from "../../Common/AppContext";
import { BetManager } from "../Managers/BetManager";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { PlayList } from "../Components/Sounds/PlayList";
import { GameManager } from "../Managers/GameManager";
import { RopeElement } from "./RopeElement";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorBonus extends WinAnimator {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static readonly WinAnimatorPrepared: string = "eventWinAnimatorPrepared";
    public static readonly TotalWinCountFinished: string = "eventTotalWinCountFinished";
    public static readonly WinAnimatorTotalStart: string = "eventWinAnimatorTotalStart";
    public static readonly MultiplierAnimationTimer: string = "multiplierAnimationTimer";
    public static readonly MultiplierAnimationTimerDelay: string = "multiplierAnimationTimerDelay";
    public static readonly ShowBigMultiplierTimer: string = "showBigMultiplierTimer";

    //******************************************************************************************************************
    //  TIMER STATUSES
    //******************************************************************************************************************

    public static readonly StickyBonusTimer: string = "stickyBonusTimer";

    public static readonly SymbolTotalWinStepAnimationTimer: string = "symbolTotalWinStepAnimationTimer";

    public static readonly TotalWinStepUpdateTimer: string = "totalWinStepUpdateTimer";

    public static readonly TotalWinCountFinishTimer: string = "totalWinCountFinishTimer";

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private static readonly SymbolTotalWinStepAnimationDuration: number = 30;
    private static readonly SymbolTotalWinStepForcedAnimationDuration: number = 10;

    private static readonly TotalWinStepUpdateDuration: number = 10;
    private static readonly TotalWinStepUpdateForcedDuration: number = 3;

    private reelsOrder: Array<number> = [0, 3, 6, 9, 12, 1, 4, 7, 10, 13, 2, 5, 8, 11, 14];

    private totalWinCounter: SwitchElement;

    private currentTotalWin: number;
    private stickyContainer: ContainerElement;
    private currentStickySymbols: Array<Array<Symbol>>;
    private fullReels: Array<number> = [0, 0, 0, 0, 0];
    private countingBlast: SwitchElement;
    private multiplierContainer: SwitchElement;
    private remainingCountToGrand: number;
    private topMultipliersContainer: SwitchElement;

    private topMultiplier3x: SwitchElement;
    private topMultiplier4x: SwitchElement;
    private topMultiplier5x: SwitchElement;
    public multiplierTeasingIndex: number = -1;
    public multiplierReelIndex: number = -1;
    private blastForce: number = 0;

    private _transition: SwitchElement;
    private _background: SwitchElement;
    protected reorderCurrentWins: Array<Communication.Game.IWin>;

    //******************************************************************************************************************
    //  CREATE, INITIALIZE
    //******************************************************************************************************************

    public initialize(
        symbols: Symbols,
        paylines?: Paylines,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
        stickyContainer?: ContainerElement,
        countingBlast?: SwitchElement,
        background?: SwitchElement,
    ): void {
        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);

        this.initializeStickyContainer(stickyContainer);
        this.countingBlast = countingBlast;
        this._background = background;
    }

    private initializeStickyContainer(stickyContainer: ContainerElement): void {
        if (stickyContainer != null) {
            this.stickyContainer = stickyContainer;

            this.currentStickySymbols = [];
            for (let i = 0; i < this.reelCount; i++) {
                this.currentStickySymbols.push([]);

                for (let j = 0; j < this.symbolCount; j++) {
                    this.currentStickySymbols[i].push(null);
                }
            }
        }
    }

    public setRemainingCountToGrand(value: number): void {
        if (value != null) {
            this.remainingCountToGrand = this.reelCount - value;

            if (BaseView.BonusCounters != null) {
                BaseView.BonusCounters.updateGrandCountToValue(
                    this.remainingCountToGrand.toString(),
                    this.remainingCountToGrand == 0 ? "EMPTY" : "SHOW",
                );
            }
        }
    }

    private decrementRemainingCount(): void {
        if (this.remainingCountToGrand > 0) {
            this.remainingCountToGrand--;

            if (BaseView.BonusCounters != null) {
                if (this.remainingCountToGrand == 0) {
                    BaseView.BonusCounters.counterGoToCase(3, "POP");
                } else {
                    BaseView.BonusCounters.updateGrandCountToValue(this.remainingCountToGrand.toString(), "POP");
                }
            }
        }
    }

    public initializeMultiplierContainer(
        multiplierContainer: SwitchElement,
        topMultipliersContainer: SwitchElement,
    ): void {
        this.multiplierContainer = multiplierContainer;
        this.topMultipliersContainer = topMultipliersContainer;
        this.topMultiplier3x = <SwitchElement>this.topMultipliersContainer.getChildElementByName(`X3`);
        this.topMultiplier4x = <SwitchElement>this.topMultipliersContainer.getChildElementByName(`X4`);
        this.topMultiplier5x = <SwitchElement>this.topMultipliersContainer.getChildElementByName(`X5`);
    }

    public resetMultiplierContainer() {
        this.multiplierGoToCase(this.topMultiplier3x, "INACTIVE");
        this.multiplierGoToCase(this.topMultiplier4x, "INACTIVE");
        this.multiplierGoToCase(this.topMultiplier5x, "INACTIVE");
        this.multiplierTeasingIndex = -1;
        this.multiplierReelIndex = -1;
    }

    //******************************************************************************************************************
    //  ON REEL STOPPED, SHOW SYMBOLS
    //******************************************************************************************************************

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        //skip already sticked symbols
        if (this.currentStickySymbols[reelIndex][0] != null) {
            return;
        }

        super.onReelStopped(reelsData, reelIndex);

        //add new sticky symbol
        if (this.currentReels[reelIndex].transform) {
            let stickySymbol = this.addStickySymbol(reelIndex);

            if (stickySymbol.getCurrentCase() != "STICKY") {
                stickySymbol.goToCase("TO_STICKY");
                this.decrementRemainingCount();

                // PlayList.play('SND_BaseBonusHit' + (reelIndex % 5));
                switch (stickySymbol.id + 1) {
                    case 9:
                        PlayList.play("SND_BonusMiniHit");
                        break;
                    case 10:
                        PlayList.play("SND_BonusMinorHit");
                        break;
                    case 11:
                        PlayList.play("SND_BonusMajorHit");
                        break;
                    default:
                        PlayList.play("SND_BaseStick");
                        break;
                }
            }

            this.updateActiveBonusCounters(this.currentReels[reelIndex].transform);
        } else {
            this.currentSymbols[reelIndex][0].goToCase(Symbol.CaseNowin);
            this.noWinSymbolsContainer.addChild(this.currentSymbols[reelIndex][0]);
        }

        this.sortSymbols();
        this.onSameSymbolsOnReelTeasingStop(reelIndex);
    }

    public openVaults(init: boolean = false) {
        let playSound = false;
        for (let i = 0; i < this.reelCount; i++) {
            if (
                (this.currentReels[i].transform || (init && this.currentReels[i].overlay)) &&
                this.currentStickySymbols[i][0]
            ) {
                let symbol = this.currentStickySymbols[i][0];
                init ? symbol.goToCase("STICKY_3") : symbol.goToCase("STICKY");
                playSound = true;
            }
        }
        if (!init && playSound) {
            PlayList.play("SND_BonusVaultOpen");
        }
    }

    public initIntroSameSymbolsOnReel(reelsData: Array<Communication.Game.IReel>) {
        for (let i = 0; i < this.reelCount; i++) {
            this.onSameSymbolsOnReel();
        }
    }

    /**
     *
     * @param reelsData
     * @param reelIndex
     */
    public multiplierGoToCase(multiplier: SwitchElement, multiplierCase: string) {
        if (multiplier != null && multiplier.getCurrentCase() !== multiplierCase) {
            multiplier.goToCase(multiplierCase);
        }
    }

    public onSameSymbolsOnReel() {
        if (this.topMultipliersContainer != null) {
            if (this.fullReels[0] >= 3 && this.fullReels[1] >= 3 && this.fullReels[2] >= 3) {
                if (this.fullReels[3] >= 3) {
                    if (this.fullReels[4] >= 3) {
                        this.multiplierGoToCase(this.topMultiplier5x, "ACTIVE");
                        this.multiplierGoToCase(this.topMultiplier4x, "INACTIVE");
                        this.multiplierGoToCase(this.topMultiplier3x, "INACTIVE");
                    } else {
                        this.multiplierGoToCase(this.topMultiplier4x, "ACTIVE");
                        this.multiplierGoToCase(this.topMultiplier3x, "INACTIVE");
                    }
                } else {
                    this.multiplierGoToCase(this.topMultiplier3x, "ACTIVE");
                }
            }
        }
    }

    public activateMultiplier(multiplier: number) {
        if (this.topMultipliersContainer != null) {
            switch (multiplier) {
                case 3:
                    this.multiplierGoToCase(this.topMultiplier5x, "INACTIVE");
                    this.multiplierGoToCase(this.topMultiplier4x, "INACTIVE");
                    this.multiplierGoToCase(this.topMultiplier3x, "ACTIVE");
                    break;
                case 4:
                    this.multiplierGoToCase(this.topMultiplier5x, "INACTIVE");
                    this.multiplierGoToCase(this.topMultiplier4x, "ACTIVE");
                    this.multiplierGoToCase(this.topMultiplier3x, "INACTIVE");
                    break;
                case 5:
                    this.multiplierGoToCase(this.topMultiplier5x, "ACTIVE");
                    this.multiplierGoToCase(this.topMultiplier4x, "INACTIVE");
                    this.multiplierGoToCase(this.topMultiplier3x, "INACTIVE");
                    break;
                default:
                    this.multiplierGoToCase(this.topMultiplier5x, "INACTIVE");
                    this.multiplierGoToCase(this.topMultiplier4x, "INACTIVE");
                    this.multiplierGoToCase(this.topMultiplier3x, "INACTIVE");
                    break;
            }
        }
    }

    public onSameSymbolsOnReelTeasingStart(multiplier: number, reelIndex: number) {
        if (this.topMultipliersContainer != null) {
            if (multiplier > this.multiplierTeasingIndex) {
                if (multiplier === 5) {
                    this.multiplierGoToCase(this.topMultiplier5x, "BOUNCING");
                } else if (multiplier === 4) {
                    this.multiplierGoToCase(this.topMultiplier4x, "BOUNCING");
                } else if (multiplier === 3) {
                    this.multiplierGoToCase(this.topMultiplier3x, "BOUNCING");
                }
            }
            this.multiplierTeasingIndex = multiplier;
            this.multiplierReelIndex = reelIndex;
        }
    }

    public onSameSymbolsOnReelTeasingStop(reelIndex: number) {
        if (this.topMultipliersContainer != null) {
            if (this.multiplierTeasingIndex > -1 && reelIndex === this.multiplierReelIndex) {
                if (this.multiplierTeasingIndex === 5 && this.topMultiplier5x.getCurrentCase() === "BOUNCING") {
                    this.multiplierGoToCase(this.topMultiplier5x, "INACTIVE");
                } else if (this.multiplierTeasingIndex === 4 && this.topMultiplier4x.getCurrentCase() === "BOUNCING") {
                    this.multiplierGoToCase(this.topMultiplier4x, "INACTIVE");
                } else if (this.multiplierTeasingIndex === 3 && this.topMultiplier3x.getCurrentCase() === "BOUNCING") {
                    this.multiplierGoToCase(this.topMultiplier3x, "INACTIVE");
                }
                this.multiplierTeasingIndex = -1;
            }
        }
    }

    public showSymbols(reelsData: Array<Communication.Game.IReel>): void {
        super.showSymbols(reelsData);

        for (let i = 0; i < this.reelCount; i++) {
            if (this.currentReels[i].overlay) {
                let stickySymbol = this.addStickySymbol(i);
                stickySymbol.goToCase("INIT");

                // this.updateActiveBonusCounters(this.currentReels[i].overlay);
            } else {
                this.currentSymbols[i][0].goToCase(Symbol.CaseNowin);
                this.noWinSymbolsContainer.addChild(this.currentSymbols[i][0]);
            }
        }
    }

    public showInitSymbols(reelsData: Array<Communication.Game.IReel>): void {
        super.showSymbols(reelsData);

        for (let i = 0; i < this.reelCount; i++) {
            if (this.currentReels[i].overlay) {
                let stickySymbol = this.addStickySymbol(i);
                stickySymbol.goToCase("INIT");

                this.updateActiveBonusCounters(this.currentReels[i].overlay);
            } else {
                this.currentSymbols[i][0].goToCase(Symbol.CaseNowin);
                this.noWinSymbolsContainer.addChild(this.currentSymbols[i][0]);
            }
        }
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    public showWins(wins: Array<Communication.Game.IWin>, win: number, transition?: SwitchElement): boolean {
        this.currentWins = wins;
        this.currentWin = win;
        this._transition = transition;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.currentMultiplierIndex = 0;

            for (let i = 0; i < this.multipliers.length; i++) {
                if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }

            if (!this.showCustomWin()) {
                // this.showFirstWins();
            }

            return true;
        } else {
            // this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());

            return false;
        }
    }

    protected showCustomWin(): boolean {
        this.stopTimer();
        this.currentCycle = -1;
        const hasGrandWin = this.hasGrandWin();

        if (hasGrandWin) {
            //reorder wins, move grand to first position
            this.currentWins = [...this.currentWins]; //clone array to remove references
            const grandWin = this.currentWins.pop();
            this.currentWins.unshift(grandWin);
        }

        this.reorderCurrentWins = [];
        for (let i = 0; i < this.currentWins.length; i++) {
            this.reorderCurrentWins.push(this.currentWins[i]);
        }

        this.reorderCurrentWins.sort((a, b) => {
            return this.reelsOrder[a.mask.indexOf(1)] - this.reelsOrder[b.mask.indexOf(1)];
        });

        if (hasGrandWin) {
            if (this._transition != null) {
                this._transition.goToCase("TO_TOTAL_COUNT_GRAND");
            }

            this.startAnimation(true);
            return true;
        }

        if (this._transition != null) {
            this._transition.goToCase("TO_TOTAL_COUNT");
        }
        this.startCountingTotalWin(hasGrandWin);
    }

    protected startCountingTotalWin(hasGrandWin?: boolean) {
        PlayList.play("SND_BonusWin");

        for (let i = 0; i < this.reorderCurrentWins.length; i++) {
            let topCounterIndex = -1;
            if (this.reorderCurrentWins[i].symbols != null) {
                topCounterIndex = this.reorderCurrentWins[i].symbols[0] - CustomUtils.SymbolBonus - 1;
            } else {
                topCounterIndex = 3;
            }

            // if (topCounterIndex > -1 && BaseView.BonusCounters != null) {
            //     BaseView.BonusCounters.counterGoToCase(topCounterIndex, 'POP');
            // }
        }

        this.startTimer(WinAnimatorBonus.ShowBigMultiplierTimer, hasGrandWin ? 20 : 80); //start counting after intro animation

        return true;
    }

    protected showBigMultiplier(): void {
        if (GameManager.bonusRespinsResponse != null && GameManager.bonusRespinsResponse.gameMultiplier > 2) {
            if (this.multiplierContainer != null) {
                // If there is a multiplier 3x or 4x or 5x, show it's GFX Container
                this.multiplierContainer.goToCase("MULTIPLIER_" + GameManager.bonusRespinsResponse.gameMultiplier);
            }

            this.stopTimer();
            this.startTimer(WinAnimatorBonus.MultiplierAnimationTimer, CustomUtils.MultiplierAnimationDuration - 15);
            return;
        }

        this.emit(WinAnimatorBonus.WinAnimatorTotalStart);
        this.showNextTotalWinStep();
    }

    protected multiplyAmounts(): void {
        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, "MULTIPLY", false);
        }

        this.stopTimer();
        this.startTimer(WinAnimatorBonus.MultiplierAnimationTimerDelay, 15);
    }

    protected showNextTotalWinStep(): void {
        if (this.currentWins == null) {
            return;
        }

        if (this.currentCycle == -1) {
            this.currentCycle = 0;
            PlayList.loop("SND_BonusBlasts");
            if (this._background) {
                let casePart = this._background.getCurrentCase().substring(0, 6);
                if (casePart != "CIRCLE") {
                    this._background.goToCase("CIRCLE");
                }
            }

            for (let i = 0; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
            }
        }

        // counting completed, start high win animation
        if (this.currentCycle == this.reorderCurrentWins.length) {
            this.startTimer(WinAnimatorBonus.TotalWinCountFinishTimer, 20);
            return;
        }

        PlayList.play("SND_BonusCountingBox");
        this.winSymbolsGoToCase(this.currentCycle, "TO_TOTAL_WIN", false);

        let delay: number;
        if (this.blastForce > 0) {
            if (
                this.blastForce === 1 &&
                this.countingBlast != null &&
                <RopeElement>this.countingBlast.getChildElementByName("Blast")
            ) {
                (<RopeElement>this.countingBlast.getChildElementByName("Blast")).setSpeed(1.2);
                this.blastForce = 2;
            }
            delay = WinAnimatorBonus.SymbolTotalWinStepForcedAnimationDuration;
        } else {
            delay = WinAnimatorBonus.SymbolTotalWinStepAnimationDuration;
        }

        // if (this.reorderCurrentWins[this.currentCycle].symbols == null) { //grand win
        //     delay = WinAnimatorBonus.SymbolTotalWinStepAnimationDuration;
        // }

        this.currentCycle++;
        this.startTimer(WinAnimatorBonus.SymbolTotalWinStepAnimationTimer, delay);
    }

    protected updateTotalWinCount(): void {
        if (this.currentCycle > 0 && this.totalWinCounter != null) {
            if (this.currentCycle > 1) {
                this.totalWinCounter.goToCase("POP");
            } else {
                const hasGrandWin = this.hasGrandWin();
                this.currentTotalWin = 0;
                this.updateTotalWinCounterValue(-1);
                this.totalWinCounter.goToCase("SHOW" + (hasGrandWin ? "_GRAND" : ""));
            }

            this.currentTotalWin = parseFloat(
                NumberUtils.add(
                    this.currentTotalWin.toString(),
                    this.reorderCurrentWins[this.currentCycle - 1].amount.toString(),
                ),
            );
            this.updateTotalWinCounterValue(this.currentTotalWin);
        }

        let delay: number;
        if (this.blastForce > 0) {
            delay = WinAnimatorBonus.TotalWinStepUpdateDuration;
        } else {
            delay = WinAnimatorBonus.TotalWinStepUpdateForcedDuration;
        }

        this.startTimer(WinAnimatorBonus.TotalWinStepUpdateTimer, delay);
    }

    protected totalWinCountFinished(): void {
        this.emit(WinAnimatorBonus.TotalWinCountFinished);

        if (this.totalWinCounter != null) {
            this.totalWinCounter.goToCase("HIDE");
        }

        this.startAnimation();
        this.emit(WinAnimatorBonus.WinAnimatorTotalStart);
    }

    protected finishFirstWins(): void {
        this.emit(WinAnimator.FinishWinAnimator);
    }

    //******************************************************************************************************************
    //  SHOW INIT WINS
    //******************************************************************************************************************

    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.currentMultiplierIndex = 0;

            for (let i = 0; i < this.multipliers.length; i++) {
                if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }

            this.showCustomInitWin();
        } else {
            // this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }
    }

    protected showCustomInitWin(): void {
        if (this.currentWins != null) {
            this.reorderCurrentWins = [];
            for (let i = 0; i < this.currentWins.length; i++) {
                this.reorderCurrentWins.push(this.currentWins[i]);
            }

            for (let i = 0; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, "TO_TOTAL_WIN_NOWIN", false);
            }

            if (this.totalWinCounter != null) {
                this.totalWinCounter.goToCase("EMPTY");
            }

            this.currentAnimationIndex = 0;
            this.showAnimationEnd();
        }
    }

    //******************************************************************************************************************
    //  CYCLE WINS
    //******************************************************************************************************************

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean): boolean {
        this.resetAnimations();
        if (this.blastForce > 0) {
            if (this.countingBlast != null && <RopeElement>this.countingBlast.getChildElementByName("Blast")) {
                (<RopeElement>this.countingBlast.getChildElementByName("Blast")).setSpeed(0.7);
            }
            this.blastForce = 0;
        }
        return super.reset(showLastSymbols);
    }

    //******************************************************************************************************************
    //  TIMER
    //******************************************************************************************************************

    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimatorBonus.MultiplierAnimationTimer:
                this.multiplyAmounts();
                break;
            case WinAnimatorBonus.MultiplierAnimationTimerDelay:
                this.emit(WinAnimatorBonus.WinAnimatorTotalStart);
                this.showNextTotalWinStep();
                break;
            case WinAnimatorBonus.ShowBigMultiplierTimer:
                this.showBigMultiplier();
                break;
            case WinAnimatorBonus.StickyBonusTimer:
                this.winAnimatorPrepared();
                break;
            case WinAnimatorBonus.SymbolTotalWinStepAnimationTimer:
                this.updateTotalWinCount();
                break;
            case WinAnimatorBonus.TotalWinStepUpdateTimer:
                this.showNextTotalWinStep();
                break;
            case WinAnimatorBonus.TotalWinCountFinishTimer:
                this.totalWinCountFinished();
                break;
            default:
                super.onTimerComplete();
        }
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    private sortSymbols(): void {
        let addQueue: Symbol[] = [];
        this.stickyContainer.children.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });

        this.stickyContainer.removeChildren();

        addQueue.sort((a, b) => a.reelIndex - b.reelIndex);
        addQueue.forEach((symbol) => {
            this.stickyContainer.addChild(symbol);
        });
    }

    protected addCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        if (this.currentSymbols[reelIndex][symbolIndex] === null) {
            let id = this.currentReels[reelIndex].symbols[symbolIndex];
            let caseType = Symbol.CaseNormal;

            //replace with bonus symbol if has overlay
            if (this.currentReels[reelIndex].overlay) {
                id = this.currentReels[reelIndex].overlay[symbolIndex];
            }

            //replace with empty symbol id
            if (!CustomUtils.isBonusSymbol(id)) {
                id = CustomUtils.SymbolEmpty;
                caseType = Symbol.CaseEmpty;
            }

            const symbol = this.symbols.getSymbol(id - 1, caseType);
            symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
            symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * symbolIndex - symbol.offsetY;

            if (this.currentReels[reelIndex].customData != null && this.currentReels[reelIndex].customData > 0) {
                let amount = this.currentReels[reelIndex].customData;

                if (amount > 0) {
                    symbol.setAmount(amount);
                }
            }

            this.symbolsContainer.addChild(symbol);

            this.currentSymbols[reelIndex][symbolIndex] = symbol;
        }
    }

    protected removeCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        super.removeCurrentSymbol(reelIndex, symbolIndex);

        if (this.currentStickySymbols != null) {
            if (this.currentStickySymbols[reelIndex][symbolIndex] !== null) {
                this.symbols.returnSymbol(this.currentStickySymbols[reelIndex][symbolIndex]);

                this.currentStickySymbols[reelIndex][symbolIndex] = null;
                this.fullReels[reelIndex % 5]--;
                if (this.fullReels[reelIndex % 5] < 0) {
                    this.fullReels[reelIndex % 5] = 0;
                }
            }
        }
    }

    protected allSymbolsGoToReset(): void {
        super.allSymbolsGoToReset();

        for (let i = 0; i < this.reelCount; i++) {
            if (this.currentSymbols[i][0] != null) {
                this.currentSymbols[i][0].goToCase(Symbol.CaseNowin);
            }
        }
    }

    protected winSymbolsGoToCaseAsScatter(winIndex: number, caseType: string): Array<Symbol> {
        if (caseType == "TO_TOTAL_WIN") {
            //pop with jackpot counter, dont pop in init
            const topCounterIndex = 3; //grand
            if (BaseView.BonusCounters != null) {
                BaseView.BonusCounters.counterGoToCase(topCounterIndex, "TOTAL_POP");
            }
            this.showBlast(-1, topCounterIndex);
        }

        return [];
    }

    protected winSymbolsGoToCaseAsCustom(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.reorderCurrentWins[winIndex];
        const symbols: Array<Symbol> = [];
        const reelIndex = win.mask.indexOf(1);

        if (reelIndex < this.currentStickySymbols.length) {
            let symbol = this.currentStickySymbols[reelIndex][0];
            const topCounterIndex = win.symbols[0] - CustomUtils.SymbolBonus - 1;

            if (symbol != null) {
                //show blast
                if (caseType == "TO_TOTAL_WIN") {
                    //pop with jackpot counter, dont pop in init
                    // if(topCounterIndex > -1 && BaseView.BonusCounters != null) {
                    //     BaseView.BonusCounters.counterGoToCase(topCounterIndex, 'POP'); //pop counter delayed by symbol win animation
                    //     PlayList.play('SND_BonusSymbol_' + topCounterIndex);
                    // }
                    this.showBlast(reelIndex, topCounterIndex);
                }

                symbol.setAmount(win.amount); //in case of symbols with level (mini, minor, major)
                symbol.goToCase(caseType);

                symbols.push(symbol);
                // this.stickyContainer.addChild(symbol);
            }
        }

        return symbols;
    }

    private showBlast(reelIndex: number, topCounterIndex: number): void {
        if (this.countingBlast != null) {
            let caseType = "SYMBOL_" + reelIndex;

            if (topCounterIndex === 3) {
                // if(topCounterIndex > -1) {
                caseType = "COUNTER_" + topCounterIndex;
            }

            this.countingBlast.goToCase(caseType);
        }
    }

    //******************************************************************************************************************
    //  PAYLINES
    //******************************************************************************************************************

    //******************************************************************************************************************
    //  ANIMATIONS
    //******************************************************************************************************************

    protected createAnimations(): void {
        super.createAnimations();

        if (this.childs["TotalWinCounter"]) {
            this.totalWinCounter = this.childs["TotalWinCounter"];
            this.totalWinCounter.goToCase("EMPTY");

            this.animationsContainer.addChild(this.value);
        }
    }

    protected startAnimation(grandWin: boolean = false): void {
        let win = this.currentWin;
        if (grandWin) {
            this.currentAnimationIndex = 1;
            win = this.reorderCurrentWins[0].amount;
            if (this._background) {
                this._background.goToCase("CIRCLE");
            }
        } else {
            this.currentAnimationIndex = 0;
            PlayList.stop("SND_BonusBlasts", 30);
            if (this._background) {
                this._background.goToCase("EMPTY");
            }
        }

        const animation = this.animations[this.currentAnimationIndex];
        animation.visible = true;

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            animation.play(parseInt(NumberUtils.devide(win, BetManager.CoinSize).toString()));
        } else {
            ImageTextCounterElement.Round = false;
            animation.play(win);
        }
    }

    protected onAnimationFinished(): void {
        if (this.currentAnimationIndex == 0) {
            super.onAnimationFinished();
        }
    }

    protected onAnimationComplete(): void {
        if (this.currentAnimationIndex == 0) {
            super.onAnimationComplete();
        } else {
            const hasGrandWin = this.hasGrandWin();
            this.startCountingTotalWin(hasGrandWin);
        }
    }

    protected showAnimationEnd(): void {
        // this.currentAnimationIndex = this.getAnimationIndex();
        this.currentAnimationFinished = true;

        const animation = this.animations[this.currentAnimationIndex];
        animation.visible = true;

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            animation.goToOutro(parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()));
        } else {
            ImageTextCounterElement.Round = false;
            animation.goToOutro(this.currentWin);
        }
    }

    private winAnimatorPrepared(): void {
        this.stopTimer();
        this.emit(WinAnimatorBonus.WinAnimatorPrepared);
    }

    public interruptAnimation(): boolean {
        if (this.currentCycle > -1 && this.currentCycle < this.reorderCurrentWins.length) {
            this.blastForce = 1;
            return false;
        } else {
            if (this.currentAnimationIndex != null) {
                const animation = this.animations[this.currentAnimationIndex];
                animation.visible = true;
                if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                    ImageTextCounterElement.Round = true;
                    animation.goToOutro(parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()));
                } else {
                    ImageTextCounterElement.Round = false;
                    animation.goToOutro(this.currentWin);
                }

                this.currentAnimationIndex = null;
                this.currentAnimationFinished = true;

                this.emit(WinAnimator.FinishWinAnimator);

                this.emit(WinAnimator.FinishWinCalculation);
            }
            return true;
        }
    }

    //******************************************************************************************************************
    //  IDLE
    //******************************************************************************************************************

    protected idle(): void {
        for (let i = 0; i < this.getIdleActiveElementsCount(); i++) {
            const symbol =
                this.currentSymbols[Math.floor(Math.random() * this.reelCount)][
                    Math.floor(Math.random() * this.symbolCount)
                ];

            if (symbol != null && symbol.getCurrentCase() === Symbol.CaseNormal) {
                if (symbol.hasCase(Symbol.CaseIdle)) {
                    symbol.goToCase(Symbol.CaseIdle);
                }
            }
        }

        this.startTimer(WinAnimator.Idle, this.getIdleDelay());
    }

    protected getInitialIdleDelay(): number {
        return 120;
    }

    protected getIdleDelay(): number {
        return 120;
    }

    //******************************************************************************************************************
    //  ADDITIONAL
    //******************************************************************************************************************

    private isGrandWin(index: number): boolean {
        if (this.currentWins != null && index < this.currentWins.length) {
            if (this.currentWins[index].trigger == Communication.Game.WinTrigger.Scatter) {
                return true;
            }
        }

        return false;
    }

    private hasGrandWin(): boolean {
        if (this.currentWins != null) {
            for (let i = 0; i < this.currentWins.length; i++) {
                if (this.isGrandWin(i)) {
                    return true;
                }
            }
        }
        return false;
    }

    private updateTotalWinCounterValue(value: number): void {
        if (this.totalWinCounter != null) {
            this.updateElementValue(<ImageTextElement>this.totalWinCounter.getChildElementByName("Amount"), value);
            this.updateElementValue(<ImageTextElement>this.totalWinCounter.getChildElementByName("AmountGlow"), value);
        }
    }

    private updateElementValue(element: ImageTextElement, value: number): void {
        if (element != null) {
            element.bitmapText.text = value > -1 ? Languages.formatText(Languages.FormatMoney, value.toString()) : "";
            element.realign();
        }
    }

    public updateActiveBonusCounters(symbols: Array<number>): void {
        for (let i = 0; i < symbols.length; i++) {
            if (CustomUtils.isBonusSymbol(symbols[i])) {
                const topCounterIndex = symbols[i] - CustomUtils.SymbolBonus - 1;

                if (topCounterIndex != 3) {
                    //if not grand (it only tease)
                    BaseView.BonusCounters.counterGoToCase(topCounterIndex, "POP");
                }
            }
        }
    }

    private addStickySymbol(reelIndex: number): Symbol {
        let stickySymbol = this.currentSymbols[reelIndex][0];
        if (stickySymbol != null) {
            stickySymbol.reelIndex = reelIndex;
            this.currentStickySymbols[reelIndex][0] = stickySymbol;
            this.currentSymbols[reelIndex][0] = null;

            this.stickyContainer.addChild(stickySymbol);

            this.fullReels[reelIndex % 5]++;
            if (this.fullReels[reelIndex % 5] > 3) {
                this.fullReels[reelIndex % 5] = 3;
            }
            this.onSameSymbolsOnReel();
        }
        return stickySymbol;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorBonus.ts
 **/